import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../components/shared/AuthContext";
// payment_voucher.amount and outstanding_amount store PAISE; the form works in RUPEES.
import { toPaise, toRupees, formatINR } from "../../utils/money";
import { DatePicker, LogoLoader } from "../../components/ui";
import useVoucherDateLock from "../../hooks/useVoucherDateLock";

const J = { primary: "#3F51B5", accent: "#2196F3", light: "#c3dbeb", lightBg: "#EEF2FB", text: "#1a237e" };
const TODAY = () => new Date().toISOString().split("T")[0];

// Microfinance payment types.
// CASH_TO_BANK is not a payment to anyone — it moves the branch's own money from
// the cash box to the bank. Without it, banking goes unrecorded and the cash
// figure climbs forever: branch 1 records Rs1.46 in for every Rs1.00 out.
//
// RETIRED from the dropdown 2026-10-01. It could never succeed: the form sends
// `destination_account_id`, which exists on neither the payment_voucher table nor
// its model, so Sequelize dropped it and postCashToBank threw "choose the bank
// account this cash was deposited into" however carefully the operator chose one.
// payment_voucher held zero rows of this type, so nothing was migrated.
//
// Transfers now live on the Receipts page as "Transfer Between Ledgers", which
// runs in BOTH directions because the source is picked explicitly rather than
// implied by the screen. The constant stays because the backend posting rule and
// its dispatch stay — removing a rule while any row might carry the type is how
// ledger entries get orphaned.
const CASH_TO_BANK_TYPE = "Cash Deposited to Bank";
// Puts members' savings back after they were lent out. Like CASH_TO_BANK it pays
// nobody — it reverses the "Borrowings from Members" receipt, moving the money
// from owed-on-term back to withdrawable. Until it is recorded the day-end stays
// blocked, so this is the only way out of that block.
const MEMBER_REPAYMENT_TYPE = "Repay Member Savings";
// Paying a funding agency back — the counterpart to the Borrowings receipt.
// Until this existed the four Borrowings accounts per branch could only grow:
// money in was recorded, money back was not. Rameswaram owes FEDERAL BANK
// Rs.1,00,000 that had no way to be cleared.
//
// Posts three legs when interest is entered:
//   Dr Borrowings - <agency>   Dr Interest Expense   Cr Cash/Bank
const AGENCY_REPAYMENT_TYPE = "Repay Funding Agency";
// Money sent to another branch of the FPO. Not an expense: one payment here also
// records the receipt at the other branch (ledgerPosting.js postInterBranchTransfer),
// and the pair's Inter-Branch accounts carry the running net position.
const INTER_BRANCH_TYPE = "Inter-Branch Transfer";
// Coins taken out of the cash box to be changed into notes. Not an expense: it
// posts Dr Coin Exchange / Cr Cash (ledgerPosting.js postCoinOut), and the notes
// coming back are a Receipt to the same Coin Exchange ledger. Always CASH; no
// Pay To — the server picks the branch's Coin Exchange account.
const COIN_OUT_TYPE = "Coin Out";
// A payment to the ledger picked in the dropdown — the mirror of a receipt:
// Cash/Bank falls, the chosen ledger rises (ledgerPosting.js postPaymentToLedger).
// Replaces the per-account types for new entries; those stay in PAYMENT_TYPES
// only so an OLD voucher still opens and re-posts exactly as it was saved.
const LEDGER_PAYMENT_TYPE = "Ledger Payment";
// The three payments that do more than "money out to one ledger", listed above
// the ledgers in the dropdown, like "Borrowings from Members" on Receipts.
const SPECIAL_TYPES = [INTER_BRANCH_TYPE, AGENCY_REPAYMENT_TYPE, MEMBER_REPAYMENT_TYPE];
// Same five legacy groups Receipts hides: their group_type is outside every
// Balance Sheet bucket, so a pair posted against one appears in no statement.
const LEGACY_GROUP_TYPES = ["BankAccount", "DirectIncome", "DirectExpenses"];
// Removed 2026-09-03:
//   "Field Officer Advance" — the FPO does not give officers advances.
//   "Refund to Member"      — a member withdrawing savings goes through Member
//                             Details, where postProceedsWithdrawal computes the
//                             proceeds-vs-savings split from their undrawn
//                             balance. A voucher route would write under a
//                             different sourceModel, so updatePostings could not
//                             recognise it as the same event and the money could
//                             be paid twice with both entries balancing.
const PAYMENT_TYPES = [
  "Loan Disbursement",
  "Salary Payment",
  "Office Expense",
  "Vendor Payment",
  // Paying out what was collected on someone else's behalf. Each debits the
  // liability it discharges; none of them touches profit.
  "Insurance Premium",
  "GST Remittance",
  "Security Deposit Refund",
  // CASH_TO_BANK_TYPE removed — see its definition above. Transfers are recorded
  // on the Receipts page now.
  MEMBER_REPAYMENT_TYPE,
  AGENCY_REPAYMENT_TYPE,
  INTER_BRANCH_TYPE,
  COIN_OUT_TYPE,
  "Other",
];
const MOP_OPTIONS = ["CASH", "BANK", "UPI", "CHEQUE"];

// Where the cash came from to put members' savings back.
//
// Recorded, not posted. By the time this payment is entered the money is already
// in the drawer — a borrower's repayment posted `Dr Cash / Cr Loans Outstanding +
// Interest` when it was collected, and a drawdown or capital receipt posted its
// own `Dr Cash` leg. Debiting a source again here would count the same rupees
// twice and claim double the money arrived.
//
// So this answers "why do we believe the money is back", for the audit trail and
// the payments list. What PROVES it is the day-end check that cash and bank
// together cover member savings.
const REPAYMENT_SOURCES = [
  "Borrower repayments (principal)",
  "Funding agency drawdown",
  "Owners' capital",
];

const EMPTY_FORM = {
  payment_no: "", date: TODAY(),
  mop: "", payment_type: "", other_description: "",
  ledger_id: "", ledger_name: "", ledger_type: "", member_unique_id: "",
  outstanding_amount: "", amount: "", narration: "",
  // Cash Deposited to Bank only: which bank account the cash lands in.
  destination_account_id: "",
  // Inter-Branch Transfer only: receiving branch and the mode it receives in;
  // from_branch_id only for a superadmin, who has no branch of their own.
  to_branch_id: "", to_mode: "", from_branch_id: "",
};

const Payments = () => {
  const { data: authData, roleName, jwt } = useAuth();
  const branchId  = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";
  // A branch admin may also correct or delete a past-dated payment — the same
  // rule as Receipts. Normalised like the server (assertVoucherDateAllowed).
  const isBranchAdmin = String(roleName || "").toLowerCase().replace(/\s+/g, "") === "admin";

  // The day a blocker is forcing this payment to be dated, or null. Read from
  // the server rather than from the button that navigated here, so the sidebar
  // route and the popup route lock identically.
  const { lockDate } = useVoucherDateLock();

  const [payments,  setPayments]      = useState([]);
  const [members,   setMembers]       = useState([]);
  const [staff,     setStaff]         = useState([]);
  const [ledgers,   setLedgers]       = useState([]);
  // Bank accounts belonging to this branch, for the Cash Deposited to Bank picker.
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading,   setLoading]       = useState(false);
  const [showModal, setShowModal]     = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  // Open but not changeable: a past payment anyone may read and only a
  // superadmin may alter.
  const [viewOnly, setViewOnly] = useState(false);
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [showSearch,  setShowSearch]  = useState(false);
  const [ledgerQuery, setLedgerQuery] = useState("");
  const [search,      setSearch]      = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  // All branches, for the Inter-Branch Transfer "To Branch" picker.
  const [branchList, setBranchList] = useState([]);

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    axios.get("/api/getModelData/branch_details", { baseURL: API_BASE_URL, params: { filter: "{}" } })
      .then(r => setBranchList(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => setBranchList([]));
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      // For vouchers: JSON filter with branchId
      const voucherFilter = isSuperAdmin ? {} : (branchId ? { branchId } : {});
      const vf = JSON.stringify(voucherFilter);

      // For members/staff/ledgers: use API's built-in branchId param (safe – skipped when null)
      const branchParam = (!isSuperAdmin && branchId) ? { branchId } : {};

      // Reads ledger_account under base_ledger_group, NOT ledger_group. That
      // table was built but never populated (0 rows on jothitest AND on live), so
      // the "Pay To" picker silently offered nothing while 82 real accounts
      // existed — searching "savings" returned "No results found" even though
      // "<branch> Savings" is there. Receipts.jsx, Balancesheet.js and
      // LedgerReport.js all read the same wrong table before they were corrected;
      // this screen was the one left behind.
      const [pvRes, mbRes, stRes, laRes, bgRes] = await Promise.all([
        axios.get("/api/getModelData/payment_voucher",  { baseURL: API_BASE_URL, params: { filter: vf } }),
        axios.get("/api/getModelData/personal_details", { baseURL: API_BASE_URL, params: { filter: '{}', ...branchParam } }),
        axios.get("/api/getModelData/staff_details",    { baseURL: API_BASE_URL, params: { filter: '{}', ...branchParam } }),
        axios.get("/api/getModelData/ledger_account",   { baseURL: API_BASE_URL, params: { filter: '{}' } }),
        axios.get("/api/getModelData/base_ledger_group", { baseURL: API_BASE_URL, params: { filter: '{}' } }),
      ]);
      const toArr = r => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];

      const groups = toArr(bgRes);
      // Branch-scoped for ordinary users; shared accounts (branchId NULL) belong
      // to no single branch and stay visible to everyone.
      const accounts = toArr(laRes)
        .filter(a => isSuperAdmin || !branchId || a.branchId == null || Number(a.branchId) === Number(branchId))
        .map(a => {
          const g = groups.find(x => Number(x.id) === Number(a.ledger_id));
          return { ...a, groupName: g?.group_name || "", groupType: g?.group_type || "" };
        });

      setPayments(toArr(pvRes));
      setMembers(toArr(mbRes));
      setStaff(toArr(stRes));
      setLedgers(accounts);
      // Only this branch's accounts in the "Bank Accounts" GROUP.
      //
      // Matching on the NAME is wrong: "Loans Outstanding - FEDERAL BANK" and
      // "Borrowings - ESAF BANK" both contain "bank" but are a receivable and a
      // liability. Depositing cash into either is meaningless, and offering them
      // invites exactly that mistake. The group says what an account IS.
      const bankGroupIds = new Set(
        groups
          .filter(g => String(g.group_name).trim().toLowerCase() === 'bank accounts')
          .map(g => g.id)
      );
      setBankAccounts(
        toArr(laRes).filter(a =>
          Number(a.branchId) === Number(branchId) && bankGroupIds.has(a.ledger_id)
        )
      );
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // The agencies this branch can repay — the four Borrowings accounts.
  //
  // Filtered by GROUP, never by name. "Loans Outstanding - FEDERAL BANK" is an
  // asset members owe US; "Borrowings - FEDERAL BANK" is what WE owe. The names
  // differ by one word and repaying against the wrong one would invert the
  // balance sheet by the full amount. The backend refuses it too, but the
  // dropdown should never offer the mistake in the first place.
  const borrowingAccounts = ledgers.filter(
    l => String(l.groupName || "").trim().toLowerCase() === "borrowings"
  );

  // Every ledger at this branch is a valid destination, because
  // postPaymentToLedger posts for any of them — except:
  //   - the account the money LEAVES (Cash for CASH, Bank otherwise);
  //   - Loans Outstanding: a loan is disbursed through Business Loan, which
  //     writes the member's statement; paying one from here would not;
  //   - the legacy groups (see LEGACY_GROUP_TYPES).
  const sourceGroup = !formData.mop ? null
    : String(formData.mop).trim().toUpperCase() === "CASH" ? "cash in hand" : "bank accounts";
  const ledgerOptions = ledgers
    .filter(l => !LEGACY_GROUP_TYPES.includes(String(l.groupType || "").trim()))
    .filter(l => {
      const g = String(l.groupName || "").trim().toLowerCase();
      return g !== "loans outstanding" && g !== sourceGroup;
    })
    .slice()
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

  // An old voucher saved under a per-account type (Office Expense, GST
  // Remittance, Coin Out, Other…). It keeps its type so editing re-posts it
  // exactly as before; new entries never get one.
  const isLegacyType = Boolean(formData.payment_type) &&
    !SPECIAL_TYPES.includes(formData.payment_type) &&
    formData.payment_type !== LEDGER_PAYMENT_TYPE;
  const isLedgerPayment = formData.payment_type === LEDGER_PAYMENT_TYPE;

  // Combined search: accounts → members → staff
  const searchEntries = [
    ...ledgers.map(l => ({
      id: l.id, label: l.name, sub: l.groupName || "Account", type: "ledger", unique_id: "",
    })),
    ...members.map(m => ({
      id: m.id,
      label: m.name || `Member #${m.id}`,
      sub: m.customerUniqueId || "",
      type: "member",
      unique_id: m.customerUniqueId || "",
    })),
    ...staff.map(s => ({
      id: s.id,
      label: s.name || `Staff #${s.id}`,
      sub: s.designation || s.username || "",
      type: "staff",
      unique_id: String(s.staffId || s.id),
    })),
  ].filter(e =>
    e.label.toLowerCase().includes(ledgerQuery.toLowerCase()) ||
    e.sub.toLowerCase().includes(ledgerQuery.toLowerCase())
  );

  const nextPaymentNo = () => {
    const nums = payments.map(p => parseInt(p.payment_no) || 0);
    return String((nums.length ? Math.max(...nums) : 0) + 1);
  };

  // A payment dated before today settles money that left on a day already
  // reported. Correcting it is an admin or superadmin action, not routine entry.
  const isPastVoucher = item => Boolean(item?.date) && item.date < TODAY();
  const canEdit = item => isSuperAdmin || isBranchAdmin || !isPastVoucher(item);

  // Admin (own branch) or superadmin. The server re-checks both. An Inter-Branch
  // Transfer takes its receipt at the other branch with it, and is refused if
  // that branch has already spent the money.
  const canDelete = isSuperAdmin || isBranchAdmin;
  const deletePayment = async (item) => {
    const amount = (Number(item.amount) || 0) / 100;   // PAISE column
    const toBranch = item.payment_type === INTER_BRANCH_TYPE
      ? (branchList.find(b => Number(b.id) === Number(item.to_branch_id))?.branchName || "the other branch")
      : null;
    const ok = window.confirm(
      `Delete payment PV-${item.payment_no} dated ${item.date} for ` +
      `Rs.${amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}?\n\n` +
      "Its ledger entries are removed too" +
      (toBranch ? `, and the receipt it created at ${toBranch}` : "") +
      ". This cannot be undone."
    );
    if (!ok) return;
    try {
      await axios.post("/api/payment-voucher/delete", { id: item.id }, {
        baseURL: API_BASE_URL,
        headers: { "x-token": jwt },
      });
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.error || "Could not delete the payment.");
    }
  };

  const openAdd = () => {
    setEditingItem(null);
    setViewOnly(false);
    // Opens ON the blocked day — see useVoucherDateLock.
    setFormData({
      ...EMPTY_FORM,
      payment_no: nextPaymentNo(),
      date: lockDate || TODAY(),
    });
    setShowModal(true);
  };

  const openEdit = item => {
    // A past payment still OPENS — it is only read-only. Refusing to open it
    // would hide the record from the person trying to understand what was
    // entered, which is the opposite of what a locked book should do: history
    // stays visible, it just stops being rewritable.
    setViewOnly(!canEdit(item));
    setEditingItem(item);
    setFormData({
      payment_no:        item.payment_no || "",
      date:              item.date || TODAY(),
      mop:               item.mop || "",
      payment_type:      item.payment_type || "",
      other_description: item.other_description || "",
      ledger_id:         item.ledger_id || "",
      ledger_name:       item.ledger_name || "",
      ledger_type:       item.ledger_type || "",
      member_unique_id:  item.member_unique_id || "",
      // Stored PAISE -> the RUPEES the input box expects. Without this a
      // reopened payment would show paise and re-saving would scale it again.
      outstanding_amount: item.outstanding_amount ? String(toRupees(item.outstanding_amount)) : "",
      amount:            item.amount ? String(toRupees(item.amount)) : "",
      narration:         item.narration || "",
      // Was omitted, so reopening a "Cash Deposited to Bank" payment blanked
      // which bank the cash went to and saving wrote the blank back — the
      // destination was silently lost on every edit.
      destination_account_id: item.destination_account_id || "",
      to_branch_id: item.to_branch_id || "",
      to_mode: item.to_mode || "",
      from_branch_id: item.branchId || "",
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingItem(null); setViewOnly(false); };
  const set = (k, v) => setFormData(p => ({ ...p, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();

    // Repaying more than was borrowed drives Borrowings from Members negative —
    // the books would assert members owe the FPO money they never received.
    // postMemberBorrowingRepaid (services/ledgerPosting.js) already throws on
    // this, but the operator should be told before pressing Save rather than
    // after, and told the figure they are allowed.
    if (formData.payment_type === MEMBER_REPAYMENT_TYPE) {
      const owed = Number(formData.outstanding_amount) || 0;
      const paying = Number(formData.amount) || 0;
      if (owed > 0 && paying > owed) {
        alert(
          `Only ₹${owed.toLocaleString("en-IN", { minimumFractionDigits: 2 })} of ` +
          `members' savings is out on loan. Enter that or less — repaying more ` +
          `would record money coming back that never went out.`
        );
        return;
      }
    }

    const sendingBranchId = editingItem?.branchId ?? branchId ?? formData.from_branch_id;
    if (formData.payment_type === INTER_BRANCH_TYPE) {
      if (!sendingBranchId) { alert("Choose the branch the money is sent FROM."); return; }
      if (!formData.to_branch_id) { alert("Choose the branch the money is sent TO."); return; }
      if (Number(formData.to_branch_id) === Number(sendingBranchId)) {
        alert("The receiving branch must be a different branch."); return;
      }
      if (!formData.to_mode) { alert("Choose how the other branch receives it (Cash or Bank)."); return; }
    }

    setLoading(true);
    try {
      const toBranchName = branchList.find(b => Number(b.id) === Number(formData.to_branch_id))?.branchName;
      const payload = {
        type: editingItem ? "Edit" : "Add",
        model: "payment_voucher",
        ...formData,
        // The operator types RUPEES; both columns store PAISE. toPaise rounds
        // rather than truncating — 12345.67 * 100 is 1234566.9999999999 in
        // binary floating point, so truncating drops a paisa on ordinary values.
        outstanding_amount: toPaise(formData.outstanding_amount),
        amount: toPaise(formData.amount),
        // A superadmin has no branch of their own, so `branchId` here is null.
        // On an Edit that would blank the voucher's branch and the ledger posting
        // fails with "could not determine the branch" — the stored row already
        // knows which branch it belongs to, so keep it.
        branchId: sendingBranchId,
        ...(formData.payment_type === INTER_BRANCH_TYPE
          ? { ledger_name: `Inter-Branch - ${toBranchName || formData.to_branch_id}`, ledger_type: "branch" }
          : { to_branch_id: null, to_mode: null }),
        ...(formData.payment_type === COIN_OUT_TYPE
          ? { mop: "CASH", ledger_id: null, ledger_name: "Coin Exchange", ledger_type: "ledger", member_unique_id: "", outstanding_amount: 0 }
          : {}),
      };
      delete payload.from_branch_id;
      if (editingItem) payload.id = editingItem.id;
      // Identifies the caller. Without it the server sees an anonymous request:
      // identifyCaller (middleware.js) leaves req.user undefined, so
      // assertVoucherDateAllowed judges even a superadmin as "not superadmin"
      // and refuses every past-dated edit — the capability existed but was
      // unreachable from this page. Same header every other page sends.
      await axios.post("/api/addDetails", payload, {
        baseURL: API_BASE_URL,
        headers: { "x-token": jwt },
      });
      await loadAll();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.error || "Error saving payment. Please try again.");
    }
    setLoading(false);
  };

  const fetchOutstanding = async (entry) => {
    setOutstandingLoading(true);
    try {
      // "Repay Member Savings" is not a party balance, so the voucher walk below
      // cannot answer it — and answered it wrongly in both directions.
      //
      // What is owed lives in the ledger as "Members' savings lent" net of
      // "...repaid". The vouchers cannot show that: the BORROWING receipts are
      // filed against the Borrowings from Members account while the REPAYMENTS
      // are filed against Savings, so a query on one ledger_id sees only half
      // the pair. On branch 2 it found the two repayments (12,335 + 1,000),
      // counted money already paid back as debt, and offered 13,335 against a
      // true balance of 9,000 — enough to overpay by 4,335 and drive the
      // liability negative.
      //
      // availableToLend returns that net as memberBorrowings, and it is the same
      // figure the day-end blocker prints. Reading it here rather than deriving
      // a second one is what keeps the two from disagreeing again.
      if (formData.payment_type === MEMBER_REPAYMENT_TYPE) {
        // Superadmin has no branch of their own; without one the endpoint 400s.
        // Leave the field empty and let the operator type it, matching how
        // useVoucherDateLock.js handles the same case.
        if (!branchId) {
          setFormData(p => ({ ...p, outstanding_amount: "" }));
          return;
        }
        const res = await axios.get("/api/ledger/funds-available", {
          baseURL: API_BASE_URL,
          params: { branchId },
        });
        const owed = Number(res.data?.data?.memberBorrowings) || 0;
        setFormData(p => ({
          ...p,
          outstanding_amount: owed > 0 ? owed.toFixed(2) : "",
        }));
        return;
      }

      if (entry.type === "member" || entry.type === "staff" || entry.type === "ledger") {
        // Replicate LedgerReport calculation:
        // Fetch all receipts and payments for this entry, then compute running balance
        const [rvRes, pvRes] = await Promise.all([
          axios.get("/api/getModelData/receipt_voucher", {
            baseURL: API_BASE_URL,
            params: { filter: JSON.stringify({ ledger_id: entry.id, ledger_type: entry.type }) }
          }),
          axios.get("/api/getModelData/payment_voucher", {
            baseURL: API_BASE_URL,
            params: { filter: JSON.stringify({ ledger_id: entry.id, ledger_type: entry.type }) }
          }),
        ]);
        const toArr = r => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];
        const receipts = toArr(rvRes);
        const payments = toArr(pvRes);

        // Build combined rows sorted by date (same as LedgerReport)
        const rows = [
          // Units differ and must be reconciled here:
          //   amount / outstanding_amount -> PAISE (converted in Stage 2)
          //   loan_outstanding            -> RUPEES (still a rupee column)
          // Normalised to rupees because the result feeds the rupee input below.
          ...payments.map(p => ({ date: p.date, debit: toRupees(p.amount), credit: 0, outstanding: toRupees(p.outstanding_amount) })),
          ...receipts.map(r => ({ date: r.date, debit: 0, credit: toRupees(r.amount), outstanding: Number(r.loan_outstanding || 0) })),
        ].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

        // Opening balance = first row's outstanding field
        const openingBal = rows.length > 0 ? rows[0].outstanding : 0;
        // Closing balance = opening + all debits - all credits
        const totalDebit  = rows.reduce((s, r) => s + r.debit, 0);
        const totalCredit = rows.reduce((s, r) => s + r.credit, 0);
        const closing = openingBal + totalDebit - totalCredit;

        setFormData(p => ({ ...p, outstanding_amount: closing > 0 ? String(closing.toFixed(2)) : "" }));
      }
    } catch (err) {
      console.log("Outstanding fetch error:", err);
    } finally {
      // `finally`, not a trailing statement: the member-savings branch above
      // returns early, which skipped the old line and left the spinner turning
      // for ever beside a figure that had already loaded. Any future early exit
      // is covered too.
      setOutstandingLoading(false);
    }
  };

  const selectLedger = entry => {
    setFormData(p => ({
      ...p,
      ledger_id:        entry.id,
      ledger_name:      entry.label,
      ledger_type:      entry.type,
      member_unique_id: entry.unique_id,
      outstanding_amount: "",
    }));
    setShowSearch(false);
    setLedgerQuery("");
    fetchOutstanding(entry);
  };

  // Re-fetch when the TYPE changes, not only when the account is picked.
  //
  // fetchOutstanding answers a different question per payment type, but it only
  // ran from selectLedger. An operator who chose the account first and the type
  // second kept whatever the earlier branch had computed — so switching to
  // "Repay Member Savings" left the voucher-walk figure on screen, which is the
  // wrong number for that type.
  useEffect(() => {
    if (!formData.ledger_id) return;
    fetchOutstanding({
      id: formData.ledger_id,
      type: formData.ledger_type,
      label: formData.ledger_name,
      unique_id: formData.member_unique_id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.payment_type]);

  const filtered = payments.filter(p =>
    (p.payment_no   || "").includes(search) ||
    (p.ledger_name  || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.payment_type || "").toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated  = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const fmt = d => {
    if (!d) return "—";
    const [y, m, dd] = d.split("-"); return `${dd}-${m}-${y}`;
  };

  const paymentTypeBadge = (type, otherDesc) => {
    const colors = {
      "Loan Disbursement":    "#2c3e50",
      "Salary Payment":       "#27ae60",
      "Field Officer Advance": "#2980b9",
      "Office Expense":       "#8e44ad",
      "Refund to Member":     "#e67e22",
      "Insurance Premium":    "#16a085",
      "Vendor Payment":       "#c0392b",
      "Other":                "#7f8c8d",
      "Ledger Payment":       "#2c7be5",
    };
    const label = (type === "Other" && otherDesc) ? otherDesc : (type || "—");
    return (
      <span style={{
        backgroundColor: colors[type] || "#7f8c8d",
        color: "#fff", borderRadius: 4,
        padding: "2px 8px", fontSize: "0.8rem",
        maxWidth: 140, display: "inline-block",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
      }} title={label}>{label}</span>
    );
  };

  const typeBadge = type => {
    const map = { ledger: ["bg-success", "Account"], member: ["bg-warning text-dark", "Member"], staff: ["bg-primary", "Staff"] };
    const [cls, label] = map[type] || ["bg-secondary", type];
    return <span className={`badge ${cls}`}>{label}</span>;
  };

  return (
    <div style={{ background: "transparent", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ background: "transparent", padding: "4px 0", marginBottom: 20, boxShadow: "none" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: "#004BB5", fontSize: "1.15rem" }}>
              <i className="fas fa-money-bill-wave me-2" />Payments
            </h5>
          </div>
          <div className="d-flex gap-2 align-items-center" style={{ flexWrap: "nowrap" }}>
            <input
              type="text"
              placeholder="Search payment / member..."
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              style={{
                borderRadius: 8,
                border: "1px solid #CBD5E1",
                background: "#fff",
                color: "#1E293B",
                padding: "4px 12px",
                height: 32,
                outline: "none",
                width: 220,
                fontSize: "0.84rem"
              }}
            />
            <button
              onClick={openAdd}
              style={{
                background: "#004BB5",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0 16px",
                height: 32,
                fontWeight: 600,
                fontSize: "0.84rem",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              + Add Payment
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 12 }}>
      <div className="table-responsive">
        <table className="table mb-0" style={{ fontSize: "0.88rem" }}>
          <thead style={{ background: J.primary, color: "#fff" }}>
            <tr>
              {["#","Payment No","Date","MOP","Payment Type","Pay To","ID / Code","Outstanding (₹)","Amount (₹)","Narration","Actions"].map(h => (
                <th key={h} style={{ padding: "12px 14px", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="text-center py-6">
                <LogoLoader size="md" />
              </td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-4 text-muted">
                No payments found. Click <strong>+ Add Payment</strong> to get started.
              </td></tr>
            ) : paginated.map((item, idx) => (
              <tr key={item.id || idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={{ padding: "12px 14px", color: "#999", fontSize: "0.82rem" }}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                <td style={{ padding: "12px 14px" }}><strong style={{ color: J.primary }}>PV-{item.payment_no}</strong></td>
                <td style={{ padding: "12px 14px" }}>{fmt(item.date)}</td>
                <td style={{ padding: "12px 14px" }}><span style={{ background: "#e0f7fa", color: "#006064", borderRadius: 6, padding: "3px 10px", fontSize: "0.78rem", fontWeight: 600 }}>{item.mop}</span></td>
                <td style={{ padding: "12px 14px" }}>{paymentTypeBadge(item.payment_type, item.other_description)}</td>
                <td style={{ padding: "12px 14px", fontWeight: 600, color: J.text }}>{item.ledger_name || "—"}</td>
                <td style={{ padding: "12px 14px" }}><code style={{ fontSize: "0.78rem", color: J.primary }}>{item.member_unique_id || "—"}</code></td>
                <td style={{ padding: "12px 14px" }}>₹{formatINR(item.outstanding_amount)}</td>
                <td style={{ padding: "12px 14px", fontWeight: 700, color: "#e74c3c" }}>
                  ₹{formatINR(item.amount)}
                </td>
                <td style={{ padding: "12px 14px", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#666" }}>
                  {item.narration || "—"}
                </td>
                <td style={{ padding: "12px 14px" }}>
                  {/* Says what it does. A past payment still opens — read-only —
                      so the button reads "View" rather than being a dead "Edit"
                      that refuses. The record stays visible; only changing it is
                      restricted. */}
                  <button
                    onClick={() => openEdit(item)}
                    title={canEdit(item) ? "" : "Dated a past day — open to read. Only an admin or superadmin can change it."}
                    style={{
                      background: J.lightBg,
                      border: `1px solid ${canEdit(item) ? J.primary : "#b0b7c3"}`,
                      color: canEdit(item) ? J.primary : "#5f6b7a",
                      borderRadius: 6,
                      padding: "4px 12px",
                      cursor: "pointer",
                      fontSize: "0.82rem",
                    }}
                  >
                    <i className={canEdit(item) ? "fas fa-edit" : "fas fa-eye"} />{" "}
                    {canEdit(item) ? "Edit" : "View"}
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => deletePayment(item)}
                      title="Delete this payment and its ledger entries"
                      style={{
                        marginLeft: 6,
                        background: "#fdecea",
                        border: "1px solid #d93025",
                        color: "#d93025",
                        borderRadius: 6,
                        padding: "4px 12px",
                        cursor: "pointer",
                        fontSize: "0.82rem",
                      }}
                    >
                      <i className="fas fa-trash" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      </div>
      {/* Pagination */}
      <div style={{ padding: "6px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6, background: "#fff", borderRadius: "0 0 12px 12px" }}>
        <div className="d-flex align-items-center gap-2 flex-nowrap" style={{ whiteSpace: "nowrap" }}>
          <span style={{ fontSize: "0.82rem", color: "#888", whiteSpace: "nowrap" }}>Rows per page:</span>
          <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            style={{ border: `1px solid ${J.light}`, borderRadius: 6, padding: "2px 6px", height: 28, fontSize: "0.82rem", outline: "none" }}>
            {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="d-flex gap-1 flex-nowrap">
          {[["First",()=>setCurrentPage(1),currentPage===1],["Prev",()=>setCurrentPage(p=>Math.max(1,p-1)),currentPage===1],["Next",()=>setCurrentPage(p=>Math.min(totalPages,p+1)),currentPage===totalPages],["Last",()=>setCurrentPage(totalPages),currentPage===totalPages]].map(([lbl,fn,dis])=>(
            <button key={lbl} onClick={fn} disabled={dis} style={{ border:`1px solid ${dis?"#ddd":J.primary}`,background:lbl==="Next"?J.primary:"#fff",color:lbl==="Next"?"#fff":dis?"#ccc":J.primary,borderRadius:6,padding:"3px 10px",height:28,fontSize:"0.82rem",cursor:dis?"not-allowed":"pointer"}}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1050,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}>
          <div style={{ background:"#fff",borderRadius:14,width:"100%",maxWidth:640,maxHeight:"90vh",overflow:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ background:`linear-gradient(135deg,${J.primary},${J.accent})`,padding:"14px 20px",color:"#fff",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:1 }}>
              <h6 className="mb-0 fw-bold"><i className="fas fa-money-bill-wave me-2"/>{viewOnly?"View":editingItem?"Edit":"Add"} Payment</h6>
              <button onClick={closeModal} style={{ background:"rgba(255,255,255,0.2)",border:"none",color:"#fff",borderRadius:6,width:28,height:28,cursor:"pointer" }}>✕</button>
            </div>
              <form onSubmit={handleSubmit}>
                {/* One disabled fieldset makes every control inside read-only —
                    inputs, selects, the date picker and the Clear Fields button —
                    so a new field added later is covered without being remembered.
                    A disabled fieldset also blocks submission, so the record can
                    be read but not rewritten. */}
                <fieldset disabled={viewOnly} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
                {viewOnly && (
                  <div style={{ background: "#fff8e1", borderBottom: "1px solid #ffe0b2", padding: "10px 28px", fontSize: "0.82rem", color: "#8a6d3b" }}>
                    <i className="fas fa-lock me-2" />
                    Dated {formData.date} — a past day. Shown for reference; only an admin or superadmin can change it.
                  </div>
                )}
                <div className="modal-body" style={{ padding: "24px 28px" }}>
                  <div className="d-flex justify-content-between align-items-center mb-4 pb-2" style={{ borderBottom: "1px dashed #e0e0e0" }}>
                    <h6 className="mb-0 fw-bold" style={{ color: J.primary }}>Transaction Details</h6>
                    <button type="button" style={{ background: "#fff5f5", color: "#e74c3c", border: "1px solid #ffe3e3", borderRadius: 6, padding: "5px 14px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}
                      onClick={() => setFormData(p => ({ ...p, mop: "", payment_type: "", ledger_id: "", ledger_name: "", ledger_type: "", member_unique_id: "", outstanding_amount: "", amount: "", narration: "" }))}>
                      <i className="fas fa-eraser me-1"/> Clear Fields
                    </button>
                  </div>

                  <div className="row g-4">
                    {/* Payment No */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Payment No</label>
                      <div className="input-group" style={{ borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                        <span className="input-group-text bg-light text-secondary fw-bold" style={{ border: "1px solid #dce0e5", borderRight: "none", padding: "10px 14px" }}>PV-</span>
                        <input className="form-control" value={formData.payment_no} readOnly style={{ backgroundColor: "#f8f9fa", border: "1px solid #dce0e5", padding: "10px 14px", fontWeight: 600, color: J.primary }} />
                      </div>
                    </div>

                    {/* Date */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Date</label>
                      {/* min === max pins the picker to exactly one day while a
                          day-end blocker stands. Undefined otherwise, so ordinary
                          entry — including legitimate backdating — is untouched. */}
                      <DatePicker
                        selected={formData.date}
                        onChange={dateStr => set("date", dateStr)}
                        minDate={lockDate ? new Date(lockDate) : undefined}
                        maxDate={lockDate ? new Date(lockDate) : undefined}
                        className="form-control w-full"
                        placeholder="DD-MM-YYYY"
                        required
                      />
                      {lockDate && (
                        <div style={{ fontSize: "0.75rem", color: J.primary, marginTop: 6 }}>
                          Fixed to the day being closed — this settlement belongs to that day.
                        </div>
                      )}
                    </div>

                    {/* Mode of Payment */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Mode of Payment</label>
                      <select className="form-select" value={formData.payment_type === COIN_OUT_TYPE ? "CASH" : formData.mop} onChange={e => set("mop", e.target.value)} required disabled={formData.payment_type === COIN_OUT_TYPE} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                        <option value="">-- Select MOP --</option>
                        {MOP_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>

                    {/* Payment Type — OLD vouchers only (see isLegacyType). */}
                    {isLegacyType && (
                    <div className={
                      formData.payment_type === "Other" || formData.payment_type === MEMBER_REPAYMENT_TYPE
                        ? "col-md-3" : "col-md-6"
                    }>
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Payment Type</label>
                      <select className="form-select" value={formData.payment_type} onChange={e => {
                        const next = e.target.value;
                        set("payment_type", next);
                        if (next === COIN_OUT_TYPE) set("mop", "CASH");
                        // other_description carries the free text for "Other" AND the
                        // source of money for a member-savings repayment. Clear it
                        // whenever the type is neither, so a leftover value from one
                        // never shows up labelled as the other.
                        if (next !== "Other" && next !== MEMBER_REPAYMENT_TYPE) set("other_description", "");
                      }} required style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                        <option value="">-- Select Type --</option>
                        {PAYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    )}

                    {/* Pay To ledger — new entries. Like the Receipts Ledger field:
                        pick where the money goes; the three special payments sit
                        above the ledgers. */}
                    {!isLegacyType && (
                    <div className={formData.payment_type === MEMBER_REPAYMENT_TYPE ? "col-md-3" : "col-md-6"}>
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                        Ledger <span className="text-danger">*</span>
                      </label>
                      <select className="form-select"
                        value={isLedgerPayment ? (formData.ledger_id ? `L:${formData.ledger_id}` : "") : formData.payment_type}
                        onChange={e => {
                          const v = e.target.value;
                          if (v.startsWith("L:")) {
                            const acc = ledgerOptions.find(a => String(a.id) === v.slice(2));
                            setFormData(p => ({
                              ...p, payment_type: LEDGER_PAYMENT_TYPE,
                              ledger_id: acc ? acc.id : "", ledger_name: acc ? acc.name : "", ledger_type: "ledger",
                              member_unique_id: "", outstanding_amount: "", other_description: "",
                            }));
                            return;
                          }
                          setFormData(p => ({
                            ...p, payment_type: v,
                            ledger_id: "", ledger_name: "", ledger_type: "",
                            member_unique_id: "", outstanding_amount: "",
                            other_description: v === MEMBER_REPAYMENT_TYPE ? p.other_description : "",
                          }));
                        }}
                        required style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                        <option value="">-- Select Ledger --</option>
                        {SPECIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        <optgroup label="────────── Ledgers ──────────">
                          {ledgerOptions.map(a => (
                            <option key={a.id} value={`L:${a.id}`}>{a.name}</option>
                          ))}
                        </optgroup>
                      </select>
                      {isLedgerPayment && formData.ledger_name && formData.mop && (
                        <div style={{ fontSize: "0.75rem", color: J.primary, marginTop: 6 }}>
                          {String(formData.mop).toUpperCase() === "CASH" ? "Cash" : "Bank"} falls; {formData.ledger_name} rises.
                        </div>
                      )}
                    </div>
                    )}

                    {/* Other Description - shown only when type is Other */}
                    {formData.payment_type === "Other" && (
                      <div className="col-md-3">
                        <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Specify Other <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" value={formData.other_description}
                          onChange={e => set("other_description", e.target.value)}
                          placeholder="Describe the payment type" required
                          style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #ffc107", backgroundColor: "#fffdf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }} />
                      </div>
                    )}

                    {/* Where the money came from. Required, because this payment moves
                        no cash of its own — it converts "owed to members on term" back
                        into "owed on demand" — so the source is the only record of why
                        the operator believes the money is actually back. */}
                    {formData.payment_type === MEMBER_REPAYMENT_TYPE && (
                      <div className="col-md-3">
                        <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Source of Money <span className="text-danger">*</span></label>
                        <select className="form-select" value={formData.other_description}
                          onChange={e => set("other_description", e.target.value)} required
                          style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #ffc107", backgroundColor: "#fffdf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                          <option value="">-- Select Source --</option>
                          {REPAYMENT_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    )}

                    {/* Cash Deposited to Bank moves the branch's own money between
                        its Cash and Bank ledgers. There is no payee, so the member
                        fields are replaced by the destination account. */}
                    {formData.payment_type === CASH_TO_BANK_TYPE && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                            Deposited Into <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select"
                            value={formData.destination_account_id}
                            onChange={e => set("destination_account_id", e.target.value)}
                            required
                            style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5" }}
                          >
                            <option value="">Select the bank account…</option>
                            {bankAccounts.map(a => (
                              <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                          </select>
                          {bankAccounts.length === 0 && (
                            <small className="d-block mt-1" style={{ color: J.danger, fontSize: "0.8rem" }}>
                              No bank account exists for this branch. Create one under Ledger Group first.
                            </small>
                          )}
                        </div>
                        <div className="col-md-6 d-flex align-items-end">
                          <div style={{ background: J.lightBg, border: "1px solid " + J.light, borderRadius: 8, padding: "10px 14px", fontSize: "0.82rem", color: J.text, width: "100%" }}>
                            Cash leaves the safe and arrives at the bank. The day-end
                            cash figure falls by this amount.
                          </div>
                        </div>
                      </>
                    )}

                    {/* Repay Funding Agency — which agency, and the interest split.
                        Replaces the member search: an agency is not a member, and
                        the four accounts are a short fixed list rather than
                        something to search for. */}
                    {formData.payment_type === INTER_BRANCH_TYPE && (
                      <>
                        {!branchId && (
                          <div className="col-md-6">
                            <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                              From Branch <span className="text-danger">*</span>
                            </label>
                            <select className="form-select" value={formData.from_branch_id}
                              onChange={e => set("from_branch_id", e.target.value)} required disabled={Boolean(editingItem)}
                              style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5" }}>
                              <option value="">Select branch…</option>
                              {branchList.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                            </select>
                          </div>
                        )}
                        <div className="col-md-6">
                          <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                            To Branch <span className="text-danger">*</span>
                          </label>
                          <select className="form-select" value={formData.to_branch_id}
                            onChange={e => set("to_branch_id", e.target.value)} required
                            style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5" }}>
                            <option value="">Select branch…</option>
                            {branchList
                              .filter(b => Number(b.id) !== Number(editingItem?.branchId ?? branchId ?? formData.from_branch_id))
                              .map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                            Received At Other Branch As <span className="text-danger">*</span>
                          </label>
                          <select className="form-select" value={formData.to_mode}
                            onChange={e => set("to_mode", e.target.value)} required
                            style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5" }}>
                            <option value="">Select…</option>
                            <option value="CASH">CASH</option>
                            <option value="BANK">BANK</option>
                          </select>
                        </div>
                        <div className="col-md-6 d-flex align-items-end">
                          <div style={{ background: J.lightBg, border: "1px solid " + J.light, borderRadius: 8, padding: "10px 14px", fontSize: "0.82rem", color: J.text, width: "100%" }}>
                            Not an expense. Money leaves this branch (Mode of Payment) and the
                            receipt is recorded at the other branch automatically.
                          </div>
                        </div>
                      </>
                    )}

                    {formData.payment_type === AGENCY_REPAYMENT_TYPE && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                            Funding Agency <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select"
                            value={formData.ledger_id}
                            onChange={e => {
                              const acc = borrowingAccounts.find(a => String(a.id) === e.target.value);
                              setFormData(p => ({
                                ...p,
                                ledger_id: e.target.value,
                                ledger_name: acc ? acc.name : "",
                                ledger_type: "ledger",
                              }));
                            }}
                            required
                            style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5" }}
                          >
                            <option value="">Select the agency being repaid…</option>
                            {borrowingAccounts.map(a => (
                              <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                          </select>
                          {borrowingAccounts.length === 0 && (
                            <small className="d-block mt-1" style={{ color: J.danger, fontSize: "0.8rem" }}>
                              No Borrowings accounts for this branch. Run scripts/create_branch_ledger_accounts.js.
                            </small>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                            Interest Included (₹)
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            value={formData.outstanding_amount}
                            onChange={e => set("outstanding_amount", e.target.value)}
                            placeholder="0.00 if this repays principal only"
                            style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5" }}
                          />
                          <small className="text-muted mt-1 d-block" style={{ fontSize: "0.8rem" }}>
                            Interest is a cost and reduces profit. The rest repays the debt
                            and changes nothing you are worth.
                          </small>
                        </div>
                      </>
                    )}

                    {/* Pay To - Search popup */}
                    {formData.payment_type !== CASH_TO_BANK_TYPE &&
                     formData.payment_type !== AGENCY_REPAYMENT_TYPE &&
                     formData.payment_type !== INTER_BRANCH_TYPE &&
                     formData.payment_type !== COIN_OUT_TYPE &&
                     formData.payment_type !== LEDGER_PAYMENT_TYPE && (
                    <div className="col-md-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                        Pay To (Member / Staff / Account) <span className="text-danger">*</span>
                      </label>
                      <div className="input-group" style={{ borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                        <input className="form-control" value={formData.ledger_name} readOnly
                          placeholder="Search member, staff or account..."
                          style={{ cursor: "pointer", backgroundColor: formData.ledger_name ? "#fff8f0" : "#fff", border: "1px solid #dce0e5", padding: "10px 14px", fontWeight: formData.ledger_name ? 600 : 400 }}
                          onClick={() => setShowSearch(true)} />
                        <button type="button" style={{ background: J.primary, border: "1px solid " + J.primary, color: "#fff", padding: "0 18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowSearch(true)}>
                          <i className="fas fa-search" />
                        </button>
                      </div>
                      {formData.member_unique_id && (
                        <small className="text-muted mt-1 d-block" style={{ fontSize: "0.8rem" }}>
                          {formData.ledger_type === "staff" ? "Staff" : "Member"} ID: <strong style={{ color: J.primary }}>{formData.member_unique_id}</strong>
                        </small>
                      )}
                    </div>
                    )}

                    {formData.payment_type === COIN_OUT_TYPE && (
                    <div className="col-12">
                      <small className="d-block" style={{ fontSize: "0.82rem", color: J.primary }}>
                        Coins leave the cash box to be changed into notes — Cash falls, Coin Exchange rises.
                        Enter the notes coming back as a Receipt to the Coin Exchange ledger (Mode CASH).
                      </small>
                    </div>
                    )}

                    {/* Outstanding - auto-fetched.
                        Hidden for an agency repayment: outstanding_amount carries
                        the INTEREST there, entered in the field above. */}
                    {formData.payment_type !== CASH_TO_BANK_TYPE &&
                     formData.payment_type !== AGENCY_REPAYMENT_TYPE &&
                     formData.payment_type !== INTER_BRANCH_TYPE &&
                     formData.payment_type !== COIN_OUT_TYPE &&
                     formData.payment_type !== LEDGER_PAYMENT_TYPE && (
                    <div className="col-md-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                        Outstanding Amount (₹)
                        {outstandingLoading && <LogoLoader size="xs" className="inline-flex ms-2 p-0" />}
                        {formData.ledger_name && !outstandingLoading && (
                          <span className="ms-2 badge" style={{ background: "#fde8e8", color: "#c0392b", fontWeight: 500, fontSize: "0.72rem", borderRadius: 6 }}>Auto-fetched</span>
                        )}
                      </label>
                      <input type="number" className="form-control" value={formData.outstanding_amount}
                        onChange={e => set("outstanding_amount", e.target.value)}
                        placeholder={formData.ledger_name ? "Fetching outstanding..." : "Select member/staff/account first"}
                        min="0" step="0.01"
                        style={{ padding: "10px 14px", borderRadius: 8, border: formData.outstanding_amount ? "1px solid #e74c3c" : "1px solid #dce0e5", backgroundColor: formData.outstanding_amount ? "#fff5f5" : "#f8f9fa", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", fontWeight: formData.outstanding_amount ? 600 : 400, color: formData.outstanding_amount ? "#c0392b" : "#999" }} />
                    </div>
                    )}

                    {/* Amount */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Payment Amount (₹) <span className="text-danger">*</span></label>
                      <input type="number" className="form-control" value={formData.amount}
                        onChange={e => set("amount", e.target.value)}
                        placeholder="Enter amount to pay" required min="1" step="0.01"
                        style={{ padding: "10px 14px", borderRadius: 8, border: "2px solid #e74c3c", backgroundColor: "#fff5f5", fontWeight: 600, color: "#e74c3c", boxShadow: "0 2px 6px rgba(231,76,60,0.15)" }} />
                    </div>

                    {/* Narration */}
                    <div className="col-12">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Narration / Remarks</label>
                      <textarea className="form-control" rows={2} value={formData.narration}
                        onChange={e => set("narration", e.target.value)}
                        placeholder="e.g. Loan disbursement for Member ID JFP-001, April 2026" style={{ padding: "12px 14px", borderRadius: 8, border: "1px solid #dce0e5", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }} />
                    </div>
                  </div>
                </div>
                </fieldset>
                {/* Outside the fieldset: closing the window must work in view mode. */}
                <div style={{ padding: "16px 28px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 12, background: "#fafafa", borderRadius: "0 0 14px 14px" }}>
                  <button type="button" onClick={closeModal} style={{ border:"1px solid #ddd",background:"#fff",color:"#666",borderRadius:8,padding:"8px 24px",cursor:"pointer" }}>{viewOnly?"Close":"Cancel"}</button>
                  {!viewOnly && (
                    <button type="submit" disabled={loading} style={{ background:`linear-gradient(135deg,${J.primary},${J.accent})`,border:"none",color:"#fff",borderRadius:8,padding:"8px 32px",fontWeight:700,cursor:loading?"not-allowed":"pointer" }}>
                      {loading?"Saving...":"Save Payment"}
                    </button>
                  )}
                </div>
              </form>
          </div>
        </div>
      )}

      {/* ── Search Popup ── */}
      {showSearch && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}>
          <div style={{ background:"#fff",borderRadius:14,width:"100%",maxWidth:540,boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ background:`linear-gradient(135deg,${J.primary},${J.accent})`,padding:"12px 18px",color:"#fff",display:"flex",justifyContent:"space-between",alignItems:"center",borderRadius:"14px 14px 0 0" }}>
              <h6 className="mb-0 fw-bold"><i className="fas fa-search me-2"/>Search Member / Staff / Account</h6>
              <button onClick={()=>{setShowSearch(false);setLedgerQuery("");}} style={{ background:"rgba(255,255,255,0.2)",border:"none",color:"#fff",borderRadius:6,width:28,height:28,cursor:"pointer" }}>✕</button>
            </div>
            <div style={{ padding:12 }}>
              <input type="text" className="form-control mb-2" placeholder="Type name, designation or ID..."
                value={ledgerQuery} onChange={e=>setLedgerQuery(e.target.value)} autoFocus />
              <div style={{ maxHeight:380,overflowY:"auto" }}>
                <table className="table table-sm table-hover mb-0">
                  <thead style={{ background:J.lightBg,position:"sticky",top:0 }}>
                    <tr><th>Name</th><th>ID / Designation</th><th>Category</th></tr>
                  </thead>
                  <tbody>
                    {searchEntries.length===0?(
                      <tr><td colSpan={3} className="text-center text-muted py-3">No results found.</td></tr>
                    ):searchEntries.map(e=>(
                      <tr key={`${e.type}_${e.id}`} style={{ cursor:"pointer" }} onClick={()=>selectLedger(e)}>
                        <td style={{ fontWeight:600,color:J.text }}>{e.label}</td>
                        <td><small style={{ color:"#888" }}>{e.sub||"—"}</small></td>
                        <td><span style={{ background:e.type==="member"?"#fff3cd":e.type==="staff"?"#dbeafe":J.lightBg,color:e.type==="member"?"#856404":e.type==="staff"?"#1d4ed8":J.primary,border:`1px solid ${e.type==="member"?"#ffc107":e.type==="staff"?"#93c5fd":J.light}`,borderRadius:6,padding:"2px 8px",fontSize:"0.75rem",fontWeight:600 }}>{e.type==="member"?"Member":e.type==="staff"?"Staff":"Account"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
